/**
 * Product surface path helpers — keep platform workflows inside Mandate shell.
 *
 * When the user is working in /mandate/*, internal links that target
 * /prioritization/items|sessions|tools|… are rewritten to /mandate/…
 */

export const PRIORITIZATION_BASE = '/prioritization';
export const MANDATE_BASE = '/apps/mandate';

const MANDATE_SHELL_KEY = 'peakPriorities_mandateShellActive';

/** Path segments that render inside the Mandate shell (Individual Space). */
export const MANDATE_EMBEDDED_SEGMENTS = new Set([
  'items',
  'sessions',
  'tools',
  'lineage',
  'reports',
  'platform',
]);

export function setMandateShellActive(active: boolean): void {
  if (typeof sessionStorage === 'undefined') return;
  if (active) {
    sessionStorage.setItem(MANDATE_SHELL_KEY, '1');
  } else {
    sessionStorage.removeItem(MANDATE_SHELL_KEY);
  }
}

export function isMandateShellActive(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(MANDATE_SHELL_KEY) === '1';
}

export function getProductBaseFromPathname(pathname: string): string {
  return pathname.startsWith(MANDATE_BASE) ? MANDATE_BASE : PRIORITIZATION_BASE;
}

/**
 * Rewrite a prioritization path to mandate when the mandate shell is active.
 * Preserves search + hash when passed a full URL string.
 */
export function rewritePathForMandateShell(raw: string): string {
  if (!raw || !isMandateShellActive()) return raw;

  let path = raw;
  let suffix = '';
  const hashIdx = path.indexOf('#');
  const queryIdx = path.indexOf('?');
  const cutIdx =
    hashIdx >= 0 && queryIdx >= 0
      ? Math.min(hashIdx, queryIdx)
      : hashIdx >= 0
        ? hashIdx
        : queryIdx >= 0
          ? queryIdx
          : -1;
  if (cutIdx >= 0) {
    suffix = path.slice(cutIdx);
    path = path.slice(0, cutIdx);
  }

  if (!path.startsWith(`${PRIORITIZATION_BASE}/`)) return raw;

  const segment = path.split('/')[2];
  if (!segment || !MANDATE_EMBEDDED_SEGMENTS.has(segment)) return raw;

  return `${MANDATE_BASE}${path.slice(PRIORITIZATION_BASE.length)}${suffix}`;
}

/** Build a path under the active product base. */
export function productPath(basePath: string, segment: string): string {
  const normalized = segment.startsWith('/') ? segment : `/${segment}`;
  if (normalized.startsWith(PRIORITIZATION_BASE) || normalized.startsWith(MANDATE_BASE)) {
    if (basePath === MANDATE_BASE && normalized.startsWith(PRIORITIZATION_BASE)) {
      return `${MANDATE_BASE}${normalized.slice(PRIORITIZATION_BASE.length)}`;
    }
    return normalized;
  }
  return `${basePath}${normalized}`;
}

/**
 * Settings is a shared hub at `/prioritization/settings/*`, but users can enter
 * it from Plans. Remember which product mode opened Settings so the Priorities /
 * Plans switcher (and Plans chrome) do not jump to Priorities on click.
 */
const SETTINGS_ENTRY_PRODUCT_KEY = 'peakPriorities_settingsEntryProduct';
export type SettingsEntryProduct = 'planning' | 'prioritization';

type SettingsEntryListener = () => void;
const settingsEntryListeners = new Set<SettingsEntryListener>();

function readSettingsEntryProduct(): SettingsEntryProduct | null {
  if (typeof sessionStorage === 'undefined') return null;
  const v = sessionStorage.getItem(SETTINGS_ENTRY_PRODUCT_KEY);
  return v === 'planning' || v === 'prioritization' ? v : null;
}

export function getSettingsEntryProduct(): SettingsEntryProduct | null {
  return readSettingsEntryProduct();
}

export function setSettingsEntryProduct(product: SettingsEntryProduct | null): void {
  if (typeof sessionStorage === 'undefined') return;
  if (product) sessionStorage.setItem(SETTINGS_ENTRY_PRODUCT_KEY, product);
  else sessionStorage.removeItem(SETTINGS_ENTRY_PRODUCT_KEY);
  settingsEntryListeners.forEach((l) => l());
}

export function subscribeSettingsEntryProduct(listener: SettingsEntryListener): () => void {
  settingsEntryListeners.add(listener);
  return () => {
    settingsEntryListeners.delete(listener);
  };
}

export function isPrioritizationSettingsPath(pathname: string): boolean {
  return pathname === '/prioritization/settings' || pathname.startsWith('/prioritization/settings/');
}

/** True when Settings was opened from Plans and we are still on a settings route. */
export function isSettingsOpenedFromPlanning(pathname: string): boolean {
  return isPrioritizationSettingsPath(pathname) && getSettingsEntryProduct() === 'planning';
}
